/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import user from 'js/lib/user';

import { Grid, Typography2 } from '@coursera/cds-core';

import type { MessageType } from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';
import { MessageTypes } from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';
import CoachIcon from 'bundles/ai-course-coach/assets/CoachIcon';
import { LearnerAvatar } from 'bundles/ai-course-coach/components/Conversation/components/Messages';
import IndeterminateIndicator from 'bundles/ai-course-coach/components/Conversation/components/Messages/components/ProgressiveLoader/IndeterminateIndicator';
import { firstNameFromFullName } from 'bundles/ai-course-coach/utils/nameParser';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  messageContainer: css`
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  senderName: css`
    color: var(--cds-color-neutral-primary);
  `,
  container: (isCoach: boolean) => css`
    display: flex;
    gap: var(--cds-spacing-100);
    background-color: ${isCoach ? 'var(--cds-color-neutral-background-primary)' : 'none'};
    padding: var(--cds-spacing-300) var(--cds-spacing-200);
    border-radius: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
  `,
  messageStyle: (isCoach: boolean) => css`
    color: var(--cds-color-neutral-primary);
    border-radius: ${isCoach
      ? 'var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200) 0'
      : 'var(--cds-spacing-200) var(--cds-spacing-200) 0 var(--cds-spacing-200)'};
  `,
};

type IntegrityCheckChatMessageProps = {
  message?: string;
  messageType: MessageType;
  submitterFirstName?: string;
};

const IntegrityCheckChatMessage = ({ message, messageType, submitterFirstName }: IntegrityCheckChatMessageProps) => {
  const isCoach = messageType === MessageTypes.COACH || messageType === MessageTypes.LOADING;
  const isLoading = messageType === MessageTypes.LOADING;

  const userName = user.get()?.fullName;
  const firstName = submitterFirstName || firstNameFromFullName(userName);
  const name = isCoach ? _t('Coach') : firstName;
  const avatar = isCoach ? (
    <CoachIcon />
  ) : (
    <LearnerAvatar
      imageUrl={submitterFirstName ? undefined : user.get().photo_120}
      name={submitterFirstName || userName}
    />
  );

  return (
    <div className="coach-message-container" css={styles.container(isCoach)}>
      {avatar}
      <Grid container css={styles.messageContainer}>
        <Grid item>
          <Typography2 variant="subtitleMedium" css={styles.senderName} component="div">
            {name}
          </Typography2>
        </Grid>
        <Grid item>
          {isLoading ? <IndeterminateIndicator /> : <div css={styles.messageStyle(isCoach)}>{message}</div>}
        </Grid>
      </Grid>
    </div>
  );
};

export default IntegrityCheckChatMessage;
